/**
 * User-Friendly Error Message Mapping
 *
 * Maps technical error messages to user-friendly messages
 * with helpful guidance on how to resolve the issue.
 */

export interface UserFriendlyError {
  title: string;
  message: string;
  helpLink?: string;
}

/**
 * Map technical error to user-friendly error
 */
export const getUserFriendlyError = (error: any): UserFriendlyError => {
  const errorMessage = error?.message || error?.toString() || 'Unknown error';
  const errorCode = error?.code || error?.status;

  // Magic Link specific errors
  if (errorMessage.toLowerCase().includes('magic link') ||
      errorMessage.toLowerCase().includes('magic url')) {
    if (errorMessage.toLowerCase().includes('expired') ||
        errorMessage.toLowerCase().includes('invalid') ||
        errorMessage.toLowerCase().includes('not found')) {
      return {
        title: 'Magic Link Expired',
        message: 'This magic link has expired or is invalid. Magic links are valid for 15 minutes. Please request a new one.',
      };
    }
    if (errorMessage.toLowerCase().includes('already used')) {
      return {
        title: 'Magic Link Already Used',
        message: 'This magic link has already been used. Please request a new one to sign in.',
      };
    }
    return {
        title: 'Magic Link Error',
      message: 'We couldn\'t verify your magic link. Please request a new one or try a different sign-in method.',
    };
  }

  // OAuth-specific errors
  if (errorMessage.toLowerCase().includes('oauth')) {
    if (errorMessage.toLowerCase().includes('invalid token')) {
      return {
        title: 'Authentication Failed',
        message: 'We couldn\'t complete your sign-in. This usually happens when the authentication link has expired. Please try signing in again.',
      };
    }
    if (errorMessage.toLowerCase().includes('cancelled') || errorMessage.toLowerCase().includes('denied')) {
      return {
        title: 'Sign-In Cancelled',
        message: 'You cancelled the sign-in process. If this was a mistake, please try again.',
      };
    }
    return {
      title: 'Authentication Error',
      message: 'Something went wrong during sign-in. Please try again or contact support if the problem persists.',
    };
  }

  // Email/password errors
  if (errorMessage.toLowerCase().includes('invalid credentials') ||
      errorMessage.toLowerCase().includes('wrong password') ||
      errorMessage.toLowerCase().includes('user not found')) {
    return {
      title: 'Invalid Credentials',
      message: 'The email or password you entered is incorrect. Please check your credentials and try again.',
    };
  }

  if (errorMessage.toLowerCase().includes('user already exists') ||
      errorMessage.toLowerCase().includes('email already in use')) {
    return {
      title: 'Account Already Exists',
      message: 'An account with this email already exists. Please sign in instead or use a different email address.',
    };
  }

  if (errorMessage.toLowerCase().includes('weak password') ||
      errorMessage.toLowerCase().includes('password too short')) {
    return {
      title: 'Weak Password',
      message: 'Your password must be at least 8 characters long. Please choose a stronger password.',
    };
  }

  if (errorMessage.toLowerCase().includes('invalid email')) {
    return {
      title: 'Invalid Email',
      message: 'Please enter a valid email address (e.g., name@company.com).',
    };
  }

  // Network errors
  if (errorCode === 401 || errorMessage.toLowerCase().includes('unauthorized')) {
    return {
      title: 'Session Expired',
      message: 'Your session has expired. Please sign in again to continue.',
    };
  }

  if (errorCode === 403 || errorMessage.toLowerCase().includes('forbidden')) {
    return {
      title: 'Access Denied',
      message: 'You don\'t have permission to perform this action. Please contact support if you believe this is an error.',
    };
  }

  if (errorCode === 429 || errorMessage.toLowerCase().includes('rate limit')) {
    return {
      title: 'Too Many Attempts',
      message: 'You\'ve made too many requests. Please wait a few minutes and try again.',
    };
  }

  if (errorMessage.toLowerCase().includes('network') ||
      errorMessage.toLowerCase().includes('fetch') ||
      errorMessage.toLowerCase().includes('connection')) {
    return {
      title: 'Connection Error',
      message: 'We couldn\'t connect to our servers. Please check your internet connection and try again.',
    };
  }

  // Server errors
  if (errorCode >= 500 || errorMessage.toLowerCase().includes('server error')) {
    return {
      title: 'Server Error',
      message: 'Our servers are experiencing issues. Please try again in a few minutes. We apologize for the inconvenience.',
    };
  }

  // Registration errors
  if (errorMessage.toLowerCase().includes('registration')) {
    if (errorMessage.toLowerCase().includes('team')) {
      return {
        title: 'Registration Error',
        message: 'We couldn\'t complete your registration. Please try again or contact support for assistance.',
      };
    }
    return {
      title: 'Registration Failed',
      message: 'Something went wrong during registration. Please try again or contact support if the problem persists.',
    };
  }

  // Profile picture errors (non-critical)
  if (errorMessage.toLowerCase().includes('profile picture') ||
      errorMessage.toLowerCase().includes('storage')) {
    return {
      title: 'Image Upload Failed',
      message: 'We couldn\'t upload your profile picture, but your account is ready to use. You can add a profile picture later from your settings.',
    };
  }

  // Webhook errors (non-critical for users)
  if (errorMessage.toLowerCase().includes('webhook') ||
      errorMessage.toLowerCase().includes('n8n')) {
    return {
      title: 'Account Created',
      message: 'Your account has been created successfully! Some background setup is still processing, but you can start using the app.',
    };
  }

  // Client ID missing errors
  if (errorMessage.toLowerCase().includes('client_id') ||
      errorMessage.toLowerCase().includes('client id')) {
    return {
      title: 'Account Setup Required',
      message: 'We need to complete your account setup before you can access this feature. This will only take a moment.',
    };
  }

  // Recovery errors
  if (errorMessage.toLowerCase().includes('recovery')) {
    return {
      title: 'Setup in Progress',
      message: 'We\'re finalizing your account configuration. Please wait a moment or contact support if the issue persists.',
    };
  }

  // Generic fallback
  return {
    title: 'Something Went Wrong',
    message: 'We encountered an unexpected error. Please try again or contact support if the problem continues.',
  };
};

/**
 * Get user-friendly validation error for registration form
 */
export const getValidationError = (field: string, value: string): string | null => {
  switch (field) {
    case 'fullName':
      if (!value.trim()) {
        return 'Please enter your full name';
      }
      if (value.trim().length < 2) {
        return 'Name must be at least 2 characters';
      }
      if (value.trim().length > 100) {
        return 'Name must be less than 100 characters';
      }
      return null;

    case 'companyName':
      if (!value.trim()) {
        return 'Please enter your company name';
      }
      if (value.trim().length < 2) {
        return 'Company name must be at least 2 characters';
      }
      if (value.trim().length > 100) {
        return 'Company name must be less than 100 characters';
      }
      return null;

    case 'companyWebsite': {
      const trimmedValue = value.trim();

      if (!trimmedValue) {
        return 'Please enter your company website';
      }

      let normalizedValue = trimmedValue;
      if (!/^https?:\/\//i.test(normalizedValue)) {
        normalizedValue = `https://${normalizedValue}`;
      }

      let parsedUrl: URL;
      try {
        parsedUrl = new URL(normalizedValue);
      } catch {
        return 'Please enter a valid company website URL';
      }

      const hostname = parsedUrl.hostname.toLowerCase().replace(/^www\./, '');
      const isLinkedInCompanyPage =
        hostname === 'linkedin.com' &&
        /^\/company\/[^/]+/i.test(parsedUrl.pathname);

      const blockedDomains = new Set([
        'google.com',
        'facebook.com',
        'reddit.com',
        'x.com',
        'twitter.com',
        'instagram.com',
        'tiktok.com',
        'youtube.com',
        'youtu.be',
        'pinterest.com',
        'snapchat.com',
        'discord.com',
        'discord.gg',
        'medium.com',
      ]);

      const blockedSuffixes = [
        '.google.com',
        '.facebook.com',
        '.reddit.com',
        '.x.com',
        '.twitter.com',
        '.instagram.com',
        '.tiktok.com',
        '.youtube.com',
        '.pinterest.com',
        '.snapchat.com',
        '.discord.com',
        '.medium.com',
      ];

      const isBlockedDomain =
        blockedDomains.has(hostname) ||
        blockedSuffixes.some((suffix) => hostname.endsWith(suffix));

      if (isBlockedDomain && !isLinkedInCompanyPage) {
        return 'Please use your corporate website or a LinkedIn company profile';
      }

      if (hostname === 'linkedin.com' && !isLinkedInCompanyPage) {
        return 'Please use a LinkedIn company page, not a personal LinkedIn profile';
      }

      const hostSegments = hostname.split('.').filter(Boolean);
      if (!isLinkedInCompanyPage && hostSegments.length < 2) {
        return 'Please enter a valid corporate website';
      }

      return null;
    }

    case 'phoneNumber':
      if (!value.trim()) {
        return null; // Phone is optional
      }
      // International phone number regex: +1234567890 or +1 (234) 567-8900
      const phoneRegex = /^\+?[1-9]\d{0,3}[\s-]?\(?[1-9]\d{0,4}\)?[\s-]?\d{1,4}[\s-]?\d{1,9}$/;
      if (!phoneRegex.test(value.replace(/[\s()-]/g, ''))) {
        return 'Please enter a valid phone number (e.g., +1 555 123-4567)';
      }
      return null;

    case 'jobTitle':
      if (!value.trim()) {
        return null; // Job title is optional
      }
      if (value.trim().length > 100) {
        return 'Job title must be less than 100 characters';
      }
      return null;

    case 'email':
      if (!value.trim()) {
        return 'Please enter your email address';
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return 'Please enter a valid email address';
      }
      return null;

    case 'password':
      if (!value) {
        return 'Please enter a password';
      }
      if (value.length < 8) {
        return 'Password must be at least 8 characters';
      }
      if (value.length > 128) {
        return 'Password must be less than 128 characters';
      }
      return null;

    default:
      return null;
  }
};
